// ClientView.jsx — v3
//
// Architecture layout :
//   Root          : width/height 100%, flex column, overflow hidden
//   Header        : flex-shrink 0, hauteur fixe
//   Body          : flex 1, position relative, overflow hidden  ← conteneur de référence
//     Main        : position absolute, inset 0, right ajuste via CSS transition
//     Panel       : position absolute, top/right/bottom fixes, slide via transform X
//     Toggle btn  : position absolute, bottom/right ajuste via CSS transition
//
// Avantages :
//   - Aucun reflow quand le contenu change (panel ne fait jamais partie du flux)
//   - Scroll garanti (main a overflow-y auto avec les 4 côtés définis)
//   - Grands écrans : contenu centré avec maxWidth dans le main
//   - Panel s'ouvre/ferme via translateX — pas d'animation de width (pas de reflow)

import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Eye,
  MessageCircle,
  Minus,
  Send,
  Sparkles,
  TrendingUp,
  Video,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePesa } from '../../stores/usePesaEngine';

// ─── Constantes layout ────────────────────────────────────────────────────────
const PANEL_W = 284; // largeur fixe du panel Coulisses (px)

// ─── Fallback thème sombre ────────────────────────────────────────────────────
const DARK = {
  bgHex:       '#020617',
  surfaceHex:  '#0f172a',
  ink:         '#f1f5f9',
  accent:      '#f59e0b',
  borderHex:   '#1e293b',
  panelBg:     'rgba(10,18,38,0.97)',
  panelBorder: 'rgba(245,158,11,0.12)',
  headerBg:    'rgba(2,6,23,0.97)',
};

// ─── Dérivation tokens locaux depuis le thème CommercialPage ─────────────────
const tok = (th) => {
  const acc = th.accent || '#f59e0b';
  const ink = th.ink    || '#f1f5f9';
  return {
    bg:          th.bgHex,
    surface:     `${th.surfaceHex || '#0f172a'}e0`,
    panel:       th.panelBg,
    header:      th.headerBg,
    border:      `${th.borderHex || '#1e293b'}`,
    borderA:     `${acc}36`,
    acc,
    accDim:      `${acc}15`,
    ink,
    inkM:        `${ink}66`,   // 40 %
    inkD:        `${ink}2e`,   // 18 %
    inkF:        `${ink}0d`,   // 5 %
    green:       '#10b981',
  };
};

// ─── Utilitaires ──────────────────────────────────────────────────────────────
const ytEmbed = (url) => {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([^&\s]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&rel=0` : null;
};

// ─── Grain ────────────────────────────────────────────────────────────────────
const Grain = () => (
  <svg aria-hidden style={{
    position:'absolute', inset:0, width:'100%', height:'100%',
    pointerEvents:'none', opacity:0.02, mixBlendMode:'overlay', zIndex:0,
  }}>
    <filter id="g2">
      <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="4" stitchTiles="stitch"/>
      <feColorMatrix type="saturate" values="0"/>
    </filter>
    <rect width="100%" height="100%" filter="url(#g2)"/>
  </svg>
);

// ─── Live pulse ───────────────────────────────────────────────────────────────
const Pulse = ({ color }) => (
  <span style={{ position:'relative', display:'inline-flex', width:8, height:8, flexShrink:0 }}>
    <motion.span
      animate={{ scale:[1,2.5,1], opacity:[0.5,0,0.5] }}
      transition={{ duration:2.4, repeat:Infinity, ease:'easeOut' }}
      style={{ position:'absolute', inset:0, borderRadius:'50%', background:color }}
    />
    <span style={{ width:8, height:8, borderRadius:'50%', background:color, display:'block' }}/>
  </span>
);

// ─── Header ───────────────────────────────────────────────────────────────────
const Header = ({ title, participants, stepIndex, totalSteps, c }) => (
  <header style={{
    flexShrink:0, height:52,
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'0 28px',
    background:c.header,
    borderBottom:`1px solid ${c.border}`,
    backdropFilter:'blur(24px)',
    position:'relative', zIndex:20,
  }}>
    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
      <Pulse color={c.green}/>
      <span style={{
        fontSize:13, fontWeight:600, fontFamily:'"DM Sans",sans-serif',
        letterSpacing:'-0.01em', color:c.ink,
      }}>
        {title || 'Session en cours'}
      </span>
    </div>

    <div style={{ display:'flex', alignItems:'center', gap:18 }}>
      {participants && (
        <span style={{ fontSize:11, color:c.inkD, fontFamily:'"DM Mono",monospace', letterSpacing:'0.04em' }}>
          {participants}
        </span>
      )}
      {totalSteps > 0 && (
        <div style={{ display:'flex', gap:5, alignItems:'center' }}>
          {Array.from({ length:totalSteps }).map((_,i) => (
            <motion.div key={i}
              animate={{
                width: i === stepIndex ? 20 : 5,
                background: i < stepIndex ? c.green : i === stepIndex ? c.acc : c.inkD,
              }}
              style={{ height:3, borderRadius:2 }}
              transition={{ duration:0.3 }}
            />
          ))}
        </div>
      )}
    </div>
  </header>
);

// ─── Médias (dans le flux de scroll) ─────────────────────────────────────────
const MediaBlock = ({ media, onClose, c }) => {
  if (!media?.url) return null;
  const embed = media.type === 'youtube' ? ytEmbed(media.url) : null;
  return (
    <motion.div
      initial={{ opacity:0, y:-8 }}
      animate={{ opacity:1, y:0 }}
      exit={{ opacity:0, y:-8 }}
      transition={{ duration:0.22 }}
      style={{ borderRadius:'1.5rem', overflow:'hidden', border:`1px solid ${c.borderA}`, background:'#000' }}
    >
      <div style={{
        display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
        background:`${c.acc}08`, borderBottom:`1px solid ${c.borderA}`,
      }}>
        <Video size={13} style={{ color:c.acc, flexShrink:0 }}/>
        <span style={{ flex:1, fontSize:11, fontFamily:'"DM Mono",monospace', letterSpacing:'0.12em', textTransform:'uppercase', color:c.inkM }}>
          {media.title || 'Média'}
        </span>
        {onClose && (
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:c.inkD, display:'flex' }}>
            <Minus size={13}/>
          </button>
        )}
      </div>
      <div style={{ position:'relative', aspectRatio:'16/9', width:'100%' }}>
        {embed
          ? <iframe src={embed} style={{ width:'100%', height:'100%', border:'none', display:'block' }} allow="autoplay; fullscreen" title={media.title}/>
          : <img src={media.url} alt={media.title||''} style={{ width:'100%', height:'100%', objectFit:'contain', display:'block' }}/>
        }
      </div>
    </motion.div>
  );
};

// ─── Choix ───────────────────────────────────────────────────────────────────
const Choice = ({ action, postAction, phaseKey, c }) => {
  const [chosen, setChosen] = useState(null);
  const [sent,   setSent]   = useState(false);

  const pick = async (opt) => {
    if (sent) return;
    setChosen(opt.id); setSent(true);
    await postAction({ type:'EVENT_TYPES.CLIENT_CHOICE', id:action.id, prompt:action.prompt, choiceId:opt.id, label:opt.label, effect:opt.effect||{}, phaseKey });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      <p style={{ fontSize:10, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkM, marginBottom:2 }}>
        {action.prompt}
      </p>
      {action.options.map((opt, idx) => {
        const sel   = chosen === opt.id;
        const other = sent && !sel;
        return (
          <motion.button key={opt.id}
            initial={{ opacity:0, x:-6 }}
            animate={{ opacity: other ? 0.12 : 1, x:0 }}
            transition={{ delay:idx*0.06, duration:0.18 }}
            onClick={() => pick(opt)}
            disabled={sent}
            style={{
              width:'100%', textAlign:'left', padding:'14px 18px', borderRadius:'1rem',
              cursor: sent ? 'default' : 'pointer',
              background: sel ? c.accDim : c.inkF,
              border:`1px solid ${sel ? c.borderA : c.border}`,
              transition:'background 0.15s,border-color 0.15s',
            }}
          >
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <motion.div animate={{ background: sel ? c.acc : c.inkD }}
                style={{ width:6, height:6, borderRadius:'50%', flexShrink:0 }}/>
              <span style={{ flex:1, fontSize:13, fontFamily:'"DM Sans",sans-serif', fontWeight: sel ? 500 : 400, color: sel ? c.acc : c.ink }}>
                {opt.label}
              </span>
              {sel && (
                <motion.span initial={{ opacity:0, scale:0.8 }} animate={{ opacity:1, scale:1 }}
                  style={{ fontSize:8, fontFamily:'"DM Mono",monospace', letterSpacing:'0.2em', textTransform:'uppercase', color:c.acc }}>
                  Envoyé ✓
                </motion.span>
              )}
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

// ─── Saisie ───────────────────────────────────────────────────────────────────
const FreeInput = ({ action, postAction, phaseKey, c }) => {
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!text.trim() || sent) return;
    setSent(true);
    await postAction({ type:'EVENT_TYPES.CLIENT_INPUT', id:action.id, prompt:action.prompt, fieldLabel:action.fieldLabel, text:text.trim(), phaseKey });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      <p style={{ fontSize:10, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkM }}>
        {action.prompt}
      </p>
      <textarea value={text} onChange={e => setText(e.target.value)} disabled={sent}
        placeholder={action.fieldLabel || 'Votre réponse…'} rows={3}
        style={{
          width:'100%', padding:'12px 16px', borderRadius:'1rem', resize:'none',
          outline:'none', boxSizing:'border-box',
          background:c.inkF, border:`1px solid ${sent ? c.borderA : c.border}`,
          color:c.ink, fontSize:13, fontFamily:'"DM Sans",sans-serif', lineHeight:1.55,
          transition:'border-color 0.15s',
        }}
        onFocus={e => { if(!sent) e.target.style.borderColor = c.borderA; }}
        onBlur={e =>  { if(!sent) e.target.style.borderColor = c.border; }}
      />
      {!sent
        ? <motion.button whileHover={{ y:-1 }} whileTap={{ scale:0.988 }} onClick={send} disabled={!text.trim()}
            style={{
              alignSelf:'flex-start', display:'flex', alignItems:'center', gap:8,
              padding:'8px 18px', borderRadius:'0.75rem',
              cursor: text.trim() ? 'pointer' : 'not-allowed',
              background: text.trim() ? `linear-gradient(135deg,${c.acc},#d97706)` : c.inkF,
              color: text.trim() ? '#fff' : c.inkM,
              border:`1px solid ${text.trim() ? c.acc : c.border}`,
              fontSize:11, fontFamily:'"DM Mono",monospace', letterSpacing:'0.12em',
            }}>
            <Send size={11}/> Envoyer
          </motion.button>
        : <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }}
            style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.2em', textTransform:'uppercase', color:c.acc }}>
            Reçu — merci
          </motion.p>
      }
    </div>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = ({ c }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16, padding:'56px 0' }}>
    <motion.div animate={{ rotate:360 }} transition={{ duration:9, repeat:Infinity, ease:'linear' }}
      style={{ width:32, height:32, borderRadius:'50%', border:`1px solid ${c.border}`, borderTopColor:c.acc }}/>
    <p style={{ fontSize:10, fontFamily:'"DM Mono",monospace', letterSpacing:'0.22em', textTransform:'uppercase', color:c.inkD }}>
      L'équipe prépare la suite…
    </p>
  </div>
);

// ─── Débrief ──────────────────────────────────────────────────────────────────
const Debrief = ({ summary, c }) => (
  <motion.div initial={{ opacity:0, scale:0.97 }} animate={{ opacity:1, scale:1 }}
    transition={{ duration:0.45, ease:[0.16,1,0.3,1] }}
    style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:24, padding:'48px 0', textAlign:'center' }}>
    <motion.div initial={{ scale:0 }} animate={{ scale:1 }}
      transition={{ delay:0.18, type:'spring', stiffness:220, damping:18 }}
      style={{ width:56, height:56, borderRadius:'50%', background:`linear-gradient(135deg,${c.acc},#d97706)`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 0 0 14px ${c.acc}12` }}>
      <Zap size={24} style={{ color:'#fff' }}/>
    </motion.div>
    <div>
      <p style={{ fontSize:10, fontFamily:'"DM Mono",monospace', letterSpacing:'0.28em', textTransform:'uppercase', color:c.acc, marginBottom:14 }}>
        Session terminée
      </p>
      <p style={{ fontSize:20, fontWeight:300, fontFamily:'"DM Sans",sans-serif', lineHeight:1.6, letterSpacing:'-0.015em', color:c.ink, maxWidth:460 }}>
        {summary || 'Merci pour votre participation.'}
      </p>
    </div>
  </motion.div>
);

// ─── Diviseur ─────────────────────────────────────────────────────────────────
const Hr = ({ c }) => (
  <div style={{ height:1, flexShrink:0, background:`linear-gradient(90deg,transparent,${c.border},transparent)` }}/>
);

// ─── Timeline ─────────────────────────────────────────────────────────────────
const Timeline = ({ current, total, labels, c }) => (
  <div style={{ display:'flex', flexDirection:'column' }}>
    {Array.from({ length:total }).map((_,i) => {
      const past = i < current, active = i === current;
      return (
        <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', width:14, flexShrink:0 }}>
            <motion.div
              animate={{ background: active ? c.acc : past ? c.green : c.inkD, scale: active ? 1.3 : 1, boxShadow: active ? `0 0 0 4px ${c.acc}22` : 'none' }}
              style={{ width:8, height:8, borderRadius:'50%', marginTop:1, flexShrink:0 }}
              transition={{ duration:0.3 }}
            />
            {i < total-1 && (
              <div style={{ width:1, flex:1, minHeight:16, marginTop:3, background: past ? c.green : c.inkD, transition:'background 0.4s' }}/>
            )}
          </div>
          <p style={{ fontSize:11, fontFamily:'"DM Sans",sans-serif', lineHeight:1.35, color: active ? c.ink : past ? c.inkM : c.inkD, fontWeight: active ? 500 : 400, paddingBottom: i < total-1 ? 14 : 0 }}>
            {labels?.[i] || `Étape ${i+1}`}
          </p>
        </div>
      );
    })}
  </div>
);

// ─── Signal de résonance ──────────────────────────────────────────────────────
const Resonance = ({ value, c }) => {
  const v = Math.max(0, Math.min(100, value ?? 0));
  const bars = 5, filled = Math.ceil(v/100*bars);
  const label = v >= 70 ? 'Favorable' : v >= 45 ? 'En cours' : 'En construction';
  const color = v >= 70 ? c.green : v >= 45 ? c.acc : c.inkD;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <span style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkD }}>Signal de résonance</span>
        <span style={{ fontSize:10, fontFamily:'"DM Mono",monospace', color, fontWeight:700 }}>{label}</span>
      </div>
      <div style={{ display:'flex', gap:4 }}>
        {Array.from({ length:bars }).map((_,i) => (
          <motion.div key={i} animate={{ background: i < filled ? color : c.inkD }}
            style={{ flex:1, height:4, borderRadius:2 }} transition={{ delay:i*0.05, duration:0.35 }}/>
        ))}
      </div>
    </div>
  );
};

// ─── Citation corpus ──────────────────────────────────────────────────────────
const Quote = ({ text, author, c }) => {
  if (!text) return null;
  return (
    <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-6 }} transition={{ duration:0.32 }}
      style={{ padding:'16px 18px', borderRadius:'1rem', background:`${c.acc}08`, border:`1px solid ${c.borderA}`, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:-14, left:12, fontSize:60, fontFamily:'Georgia,serif', color:`${c.acc}08`, lineHeight:1, userSelect:'none', pointerEvents:'none' }}>"</div>
      <p style={{ fontSize:12, fontFamily:'"DM Sans",sans-serif', fontStyle:'italic', lineHeight:1.65, color:`${c.acc}cc`, position:'relative' }}>
        {text}
      </p>
      {author && (
        <p style={{ marginTop:8, fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.15em', color:c.inkD }}>— {author}</p>
      )}
    </motion.div>
  );
};

// ─── Contenu panneau Coulisses ────────────────────────────────────────────────
const PanelContent = ({ theory, metadata, stepIndex, totalSteps, stepLabels, probability, currentQuote, c }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:20, padding:'24px 20px', boxSizing:'border-box', minHeight:'100%' }}>
    {/* En-tête */}
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
        <Sparkles size={12} style={{ color:c.acc }}/>
        <span style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.22em', textTransform:'uppercase', color:c.acc }}>
          Analyse en direct
        </span>
      </div>
      {metadata?.estimatedDuration && (
        <p style={{ fontSize:11, color:c.inkM, fontFamily:'"DM Sans",sans-serif', lineHeight:1.5 }}>
          Durée estimée : {metadata.estimatedDuration}
        </p>
      )}
    </div>

    <Hr c={c}/>

    {/* Concept */}
    {theory?.primaryConcept && (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
          <BookOpen size={10} style={{ color:c.inkD }}/>
          <span style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkD }}>Approche</span>
        </div>
        <p style={{ fontSize:13, fontFamily:'"DM Sans",sans-serif', fontWeight:500, color:c.ink, lineHeight:1.5, letterSpacing:'-0.01em' }}>
          {theory.primaryConcept}
        </p>
        {theory.secondaryConcept && (
          <p style={{ marginTop:6, fontSize:11, fontFamily:'"DM Sans",sans-serif', color:c.inkM, lineHeight:1.5, fontStyle:'italic' }}>
            {theory.secondaryConcept}
          </p>
        )}
      </div>
    )}

    {/* Citation */}
    <AnimatePresence mode="wait">
      <Quote key={currentQuote?.text || theory?.quote || 'noquote'}
        text={currentQuote?.text || theory?.quote}
        author={currentQuote?.author || theory?.author}
        c={c}
      />
    </AnimatePresence>

    <Hr c={c}/>

    {/* Timeline */}
    {totalSteps > 0 && (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
          <TrendingUp size={10} style={{ color:c.inkD }}/>
          <span style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkD }}>
            Progression — {stepIndex+1}/{totalSteps}
          </span>
        </div>
        <Timeline current={stepIndex} total={totalSteps} labels={stepLabels} c={c}/>
      </div>
    )}

    {/* Résonance */}
    {probability != null && (
      <>
        <Hr c={c}/>
        <Resonance value={probability} c={c}/>
      </>
    )}

    {/* Source */}
    {theory?.source && (
      <>
        <Hr c={c}/>
        <div>
          <span style={{ fontSize:9, fontFamily:'"DM Mono",monospace', letterSpacing:'0.18em', textTransform:'uppercase', color:c.inkD, display:'block', marginBottom:6 }}>
            Référence
          </span>
          <p style={{ fontSize:11, fontFamily:'"DM Sans",sans-serif', color:c.inkD, lineHeight:1.55 }}>
            <em>{theory.source.title}</em>
            {theory.source.author && ` — ${theory.source.author}`}
            {theory.source.year && `, ${theory.source.year}`}
          </p>
        </div>
      </>
    )}
  </div>
);


// ─── Helpers ─────────────────────────────────────────────────────────────────
const pick = (val, fallback) => (val !== undefined && val !== null && val !== '' ? val : fallback);

// Mapping ton → palette (même logique que DebriefScene pour cohérence)
const TONE_COLORS = {
  radieux:    '#10b981',
  lucide:     null, // sera remplacé par theme.accent
  sombre:     '#ef4444',
  cynique:    '#94a3b8',
  tragique:   '#ef4444',
  désespéré:  '#ef4444',
};

// Petits ornements de luxe
const Ornament = ({ color }) => (
  <svg width="48" height="8" viewBox="0 0 48 8" fill="none" style={{ opacity: 0.35 }}>
    <line x1="0" y1="4" x2="16" y2="4" stroke={color} strokeWidth="0.75" />
    <circle cx="24" cy="4" r="2.5" stroke={color} strokeWidth="0.75" />
    <line x1="32" y1="4" x2="48" y2="4" stroke={color} strokeWidth="0.75" />
  </svg>
);

// ─── Stagger children helper ──────────────────────────────────────────────────
const FadeUp = ({ delay = 0, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

// ─── Composant principal ──────────────────────────────────────────────────────
export const ClientDebriefScene = ({ scenario, completedActions, theme, clientSummary }) => {
  // clientSummary : texte libre fourni par le vendeur/animateur (optionnel)
  // On peut aussi dériver un message client depuis matched.interpretation.clientMessage si défini dans le scénario

  const safeCompleted = completedActions instanceof Set
    ? [...completedActions]
    : (completedActions || []);

  // Résoudre la meilleure solution (même algorithme que DebriefScene)
  const matched = useMemo(() => {
    if (!scenario?.solutions?.length) return null;
    return scenario.solutions.reduce((best, sol) => {
      const req   = sol.requiredActions || [];
      const score = req.length > 0
        ? req.filter(a => safeCompleted.includes(a)).length / req.length
        : 0;
      return score > (best?.score ?? -1) ? { ...sol, score } : best;
    }, null);
  }, [scenario?.solutions, safeCompleted]);

  const tone      = matched?.interpretation?.tone || 'lucide';
  const toneColor = TONE_COLORS[tone] ?? theme?.accent ?? '#6366f1';
  const ink       = theme?.ink ?? '#1a1a2e';
  const accent    = theme?.accent ?? '#6366f1';
  const panelBg   = theme?.panelBg ?? '#ffffff';

  // Message affiché au client : priorité clientMessage > clientSummary > shortSummary adapté > fallback
  const clientMessage = pick(
    matched?.interpretation?.clientMessage,
    pick(clientSummary, pick(matched?.interpretation?.shortSummary, 'Merci pour votre participation.'))
  );

  // Insight client si défini dans le scénario (différent du keyInsight vendeur)
  const clientInsight = matched?.interpretation?.clientInsight;

  // Questions ouvertes (max 2) — si le scénario les expose au client
  const openQuestions = !matched?.interpretation?.hideQuestionsFromClient
    ? (matched?.interpretation?.openQuestions || []).slice(0, 2)
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}
    >
      {/* ── Carte principale ─────────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          background: panelBg,
          border: `1px solid ${toneColor}22`,
          borderRadius: '1.75rem',
          padding: '2.5rem 2.5rem 2rem',
          boxShadow: `0 0 0 1px ${toneColor}06, 0 16px 64px ${toneColor}08`,
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        {/* Ligne de lumière haute */}
        <div style={{
          position: 'absolute', top: 0, left: '10%', right: '10%', height: 1,
          background: `linear-gradient(90deg, transparent, ${toneColor}50, transparent)`,
        }} />

        {/* Icône premium animée */}
        <FadeUp delay={0.1}>
          <motion.div
            initial={{ scale: 0, rotate: -15 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.18, type: 'spring', stiffness: 240, damping: 18 }}
            style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 20px',
              background: `linear-gradient(135deg, ${toneColor}, ${accent})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 0 0 14px ${toneColor}10, 0 8px 24px ${toneColor}30`,
            }}
          >
            <Sparkles size={22} style={{ color: '#fff' }} />
          </motion.div>
        </FadeUp>

        {/* Label session */}
        <FadeUp delay={0.22}>
          <p style={{
            fontSize: 9, fontFamily: '"DM Mono", monospace',
            letterSpacing: '0.28em', textTransform: 'uppercase',
            color: `${toneColor}70`, marginBottom: 16,
          }}>
            Session terminée
          </p>
        </FadeUp>

        {/* Ornement */}
        <FadeUp delay={0.28}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <Ornament color={toneColor} />
          </div>
        </FadeUp>

        {/* Message principal */}
        <FadeUp delay={0.32}>
          <p style={{
            fontSize: 20, fontWeight: 300, lineHeight: 1.65,
            fontFamily: '"DM Sans", sans-serif', letterSpacing: '-0.018em',
            color: ink, maxWidth: 480, margin: '0 auto',
          }}>
            {clientMessage}
          </p>
        </FadeUp>

        {/* Insight client */}
        {clientInsight && (
          <FadeUp delay={0.42}>
            <div style={{
              marginTop: 20, paddingTop: 16,
              borderTop: `1px solid ${ink}08`,
            }}>
              <p style={{
                fontSize: 13, lineHeight: 1.6, fontStyle: 'italic',
                fontFamily: '"DM Sans", sans-serif', color: `${ink}45`,
              }}>
                {clientInsight}
              </p>
            </div>
          </FadeUp>
        )}
      </div>

      {/* ── Questions pour aller plus loin ───────────────────────────────── */}
      {openQuestions.length > 0 && (
        <FadeUp delay={0.5}>
          <div style={{
            background: `${accent}05`,
            border: `1px solid ${accent}12`,
            borderRadius: '1.25rem',
            padding: '1rem 1.25rem',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
              <MessageCircle size={11} style={{ color: `${accent}55` }} />
              <p style={{
                fontSize: 8, fontFamily: '"DM Mono", monospace',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: `${accent}50`, margin: 0,
              }}>
                Pour aller plus loin
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {openQuestions.map((q, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55 + i * 0.08 }}
                  style={{
                    fontSize: 12, lineHeight: 1.6, fontStyle: 'italic',
                    fontFamily: '"DM Sans", sans-serif',
                    color: `${ink}50`, margin: 0,
                  }}
                >
                  {q}
                </motion.p>
              ))}
            </div>
          </div>
        </FadeUp>
      )}

      {/* ── Confirmation de participation ─────────────────────────────────── */}
      <FadeUp delay={0.6}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '0.75rem 1rem',
          background: `${toneColor}06`,
          border: `1px solid ${toneColor}14`,
          borderRadius: '1rem',
        }}>
          <CheckCircle2 size={13} style={{ color: `${toneColor}60`, flexShrink: 0 }} />
          <p style={{
            fontSize: 11, fontFamily: '"DM Sans", sans-serif',
            color: `${ink}38`, margin: 0, lineHeight: 1.4,
          }}>
            Votre participation a bien été enregistrée.
          </p>
        </div>
      </FadeUp>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export const ClientView = ({ snapshot, lastEvent, postAction, theme: externalTheme }) => {
  const { scenario, completedActions, } = usePesa();  
  
    const commercialLive = usePesa(s => s.commercialLive) ?? {
        enabled: false, token: null, role: null, connected: false,
        clientEvents: [], pendingClientEvents: [], compiledClientEvents: [],
        clientInputs: [], clientChoices: [],
    };
  const c = tok(externalTheme || DARK);

  const [panelOpen,  setPanelOpen]  = useState(true);
  const [localMedia, setLocalMedia] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (snapshot?.activeMedia) setLocalMedia(snapshot.activeMedia);
  }, [snapshot?.activeMedia]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top:0, behavior:'smooth' });
  }, [snapshot?.clientNarrative]);

  

  // ── Loading ───────────────────────────────────────────────────────────────
  if (!snapshot) {
    return (
      <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:c.bg, gap:16, position:'relative', overflow:'hidden' }}>
        <Grain/>
        <motion.div animate={{ rotate:360 }} transition={{ duration:7, repeat:Infinity, ease:'linear' }}
          style={{ width:30, height:30, borderRadius:'50%', border:`1px solid ${c.border}`, borderTopColor:c.acc }}/>
        <p style={{ fontSize:10, fontFamily:'"DM Mono",monospace', letterSpacing:'0.22em', textTransform:'uppercase', color:c.inkD }}>
          Connexion en cours…
        </p>
      </div>
    );
  }

  const {
    clientNarrative, clientQuestion, clientActions,
    isFinished, debriefSummary,
    activeMedia: snapMedia,
    theory, metadata,
    stepIndex    = 0,
    totalSteps   = 0,
    stepLabels,
    probability,
    currentCorpusQuote,
    sessionTitle, participants, phaseKey,
  } = snapshot;

  const media        = localMedia || snapMedia;
  const showActions  = !!clientActions?.length && !isFinished;
  const showWaiting  = !showActions && !isFinished && !clientNarrative;
  const hasCoulisses = !!(theory || totalSteps > 0 || metadata);

  // Décalage du main quand le panel est ouvert
  const mainRight = hasCoulisses && panelOpen ? PANEL_W : 0;

  return (
    // Racine : occupe 100 % du conteneur (CommercialPage → fixed inset-0 z-10)
    <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', background:c.bg, color:c.ink, overflow:'hidden', position:'relative' }}>
      <Grain/>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Header title={sessionTitle} participants={participants} stepIndex={stepIndex} totalSteps={totalSteps} c={c}/>

      {/* ── Body : conteneur de référence pour les positionnements abs ── */}
      <div style={{ flex:1, position:'relative', overflow:'hidden' }}>

        {/* Main : position absolute, right s'adapte via CSS transition */}
        <main
          ref={scrollRef}
          style={{
            position:'absolute',
            top:0, left:0, bottom:0,
            right: mainRight,
            overflowY:'auto',
            overflowX:'hidden',
            scrollbarWidth:'none',
            transition:'right 0.28s cubic-bezier(0.25,0.46,0.45,0.94)',
          }}
        >
          {/* Conteneur centré pour grands écrans */}
          <div style={{
            maxWidth:700,
            margin:'0 auto',
            padding:'36px 40px 96px',
            display:'flex',
            flexDirection:'column',
            gap:24,
            boxSizing:'border-box',
          }}>
            {isFinished
              ? <ClientDebriefScene
                  scenario={scenario}
                  completedActions={completedActions}
                  theme={tok}
                  clientSummary={commercialLive?.clientSummary} // optionnel, envoyé par le vendeur
                />
              : (
                <>
                  {/* Média */}
                  <AnimatePresence>
                    {media && <MediaBlock key="media" media={media} onClose={() => setLocalMedia(null)} c={c}/>}
                  </AnimatePresence>

                  {/* Narrative */}
                  <AnimatePresence mode="wait">
                    {clientNarrative && (
                      <motion.div key={clientNarrative}
                        initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-8 }}
                        transition={{ duration:0.28 }}
                        style={{ padding:'28px 32px', borderRadius:'1.75rem', background:c.surface, border:`1px solid ${c.border}`, position:'relative', overflow:'hidden' }}
                      >
                        <div style={{ position:'absolute', top:0, left:'14%', right:'14%', height:1, background:`linear-gradient(90deg,transparent,${c.borderA},transparent)` }}/>
                        <p style={{ fontSize:17, fontWeight:300, lineHeight:1.72, fontFamily:'"DM Sans",sans-serif', letterSpacing:'-0.015em', color:c.ink, textAlign:'center', margin:0 }}>
                          {clientNarrative}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Question */}
                  <AnimatePresence>
                    {clientQuestion && (
                      <motion.div key={clientQuestion}
                        initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                        transition={{ delay:0.1, duration:0.22 }}
                        style={{ padding:'16px 24px', borderRadius:'1.25rem', background:c.accDim, border:`1px solid ${c.acc}20` }}
                      >
                        <p style={{ fontSize:15, fontWeight:300, fontStyle:'italic', lineHeight:1.6, color:c.ink, fontFamily:'"DM Sans",sans-serif', textAlign:'center', letterSpacing:'-0.01em', margin:0 }}>
                          {clientQuestion}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Actions */}
                  {showActions && (
                    <motion.div initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.15, duration:0.22 }}
                      style={{ display:'flex', flexDirection:'column', gap:20 }}>
                      {clientActions.map((action, idx) => (
                        <motion.div key={action.id}
                          initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
                          transition={{ delay:idx*0.07, duration:0.18 }}>
                          {action.type === 'choice' && <Choice action={action} postAction={postAction} phaseKey={phaseKey||''} c={c}/>}
                          {action.type === 'input'  && <FreeInput action={action} postAction={postAction} phaseKey={phaseKey||''} c={c}/>}
                        </motion.div>
                      ))}
                    </motion.div>
                  )}

                  {/* Attente */}
                  {showWaiting && <Spinner c={c}/>}
                </>
              )
            }
          </div>
        </main>

        {/* Panel Coulisses : position absolute, top/right/bottom fixes → jamais dans le flux */}
        {hasCoulisses && (
          <motion.aside
            initial={false}
            animate={{ x: panelOpen ? 0 : PANEL_W }}
            transition={{ duration:0.28, ease:[0.25,0.46,0.45,0.94] }}
            style={{
              position:'absolute',
              top:0, right:0, bottom:0,
              width:PANEL_W,
              borderLeft:`1px solid ${c.border}`,
              background:c.panel,
              backdropFilter:'blur(20px)',
              overflowY:'auto',
              overflowX:'hidden',
              scrollbarWidth:'none',
              zIndex:10,
              // Le panel coulisse via translateX — aucun impact sur le flux
            }}
          >
            <PanelContent
              theory={theory} metadata={metadata}
              stepIndex={stepIndex} totalSteps={totalSteps} stepLabels={stepLabels}
              probability={probability} currentQuote={currentCorpusQuote}
              c={c}
            />
          </motion.aside>
        )}

        {/* Bouton toggle — position absolute, right ajuste via CSS transition */}
        {hasCoulisses && (
          <button
            onClick={() => setPanelOpen(v => !v)}
            style={{
              position:'absolute',
              bottom:20,
              right: panelOpen ? PANEL_W + 12 : 20,
              display:'flex', alignItems:'center', gap:6,
              padding:'6px 14px', borderRadius:'2rem',
              background:c.inkF, border:`1px solid ${c.border}`,
              cursor:'pointer', zIndex:20,
              fontSize:9, fontFamily:'"DM Mono",monospace',
              letterSpacing:'0.2em', textTransform:'uppercase', color:c.inkD,
              transition:'right 0.28s cubic-bezier(0.25,0.46,0.45,0.94), opacity 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {panelOpen
              ? <><ChevronRight size={10}/> Réduire</>
              : <><Eye size={10}/> Coulisses</>
            }
          </button>
        )}
      </div>
    </div>
  );
};

export default ClientView;
